import { DateTime } from 'luxon';
import { DateObject } from 'react-multi-date-picker';
import { useTheme } from '@mui/styles';
import { Box, styled, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { dateTimeToDateObject } from 'shared/components/shift-forms/components/shift-multiply-calendar/multi-date-object-utils';
import { useGetWorkerAvailability } from './use-get-worker-availability';
import { SelectedDateWithAvailability } from '../wizard/availability-sidebar';
import { isOverlappingSegment } from '../utils/is-overlapping-segment';
import { SegmentsForEdit, TimeSlotFormContext } from '../wizard/time-slot.types';

export const useCalendarMethods = ({
  isCreateMode,
  selectedDates,
  setSelectedDates,
  monthAvailabilityQuery,
}: {
  isCreateMode: boolean;
  selectedDates: SelectedDateWithAvailability[];
  setSelectedDates: React.Dispatch<React.SetStateAction<SelectedDateWithAvailability[]>>;
  monthAvailabilityQuery: ReturnType<typeof useGetWorkerAvailability>;
}) => {
  const theme = useTheme();
  const { watch } = useFormContext<TimeSlotFormContext>();
  const timeslots = watch('availability');
  const mapRelatedAvailabilityToSegmentForEdit = (availability: {
    segmentId: string;
    startTime: DateTime;
    endTime: DateTime;
  }) => ({
    segmentId: availability.segmentId,
    utcStartDateTime: availability.startTime.toFormat('T'),
    utcEndDateTime: availability.endTime.toFormat('T'),
  });

  const handleDateSelection = (selectedDate: DateObject | DateObject[] | null) => {
    if (isCreateMode && selectedDate) {
      const dateObject = Array.isArray(selectedDate) ? selectedDate[0] : selectedDate;
      if (dateObject && dateObject.toDate) {
        const newDate = DateTime.fromJSDate(dateObject.toDate());
        const alreadySelectedDate = selectedDates.some((selectedDate) =>
          selectedDate.date.startOf('day').equals(newDate.startOf('day'))
        );
        if (!alreadySelectedDate) {
          setSelectedDates((prev) => [...prev, { date: newDate }]);
        } else {
          setSelectedDates((prev) =>
            prev.length > 1
              ? prev.filter(
                  (selectedDate) => !selectedDate.date.startOf('day').equals(newDate.startOf('day'))
                )
              : prev
          );
        }
      }
    }
  };

  const doesDateContainOverlappingTimeSlot = (selectedDate: SelectedDateWithAvailability) => {
    if (selectedDate.relatedAvailability) {
      return selectedDate.relatedAvailability.some((availability) => {
        const availabilityAsSegment: SegmentsForEdit =
          mapRelatedAvailabilityToSegmentForEdit(availability);
        return isOverlappingSegment(availabilityAsSegment, timeslots);
      });
    }
    return false;
  };

  const handleDayMapping = ({
    date,
    isSameDate,
  }: {
    date: DateObject;
    isSameDate: (a: DateObject, b: DateObject) => boolean;
  }) => {
    let isErrored = false;
    let backgroundColor = 'inherit';
    const selectedDate = selectedDates.find((selectedDate) =>
      isSameDate(dateTimeToDateObject(selectedDate.date), date)
    );
    const isSelected = !!selectedDate;
    const hasRelatedAvailability =
      !!monthAvailabilityQuery.data?.availabilities[
        DateTime.fromJSDate(date.toDate()).toFormat('dd-MM-yyyy')
      ];
    if (isSelected && !!selectedDate.relatedAvailability) {
      isErrored = doesDateContainOverlappingTimeSlot(selectedDate);
    }
    if (isErrored) {
      backgroundColor = theme.palette.lwDanger[20];
    } else if (isSelected) {
      backgroundColor = theme.palette.lwPrimaryAccentLight[20];
    }

    return {
      children: (
        <DateContainer>
          <StyledTypography variant="body2" color="inherit" backgroundColor={backgroundColor}>
            {date.format('DD')}
          </StyledTypography>
          {hasRelatedAvailability && isCreateMode ? (
            <AvailabilityBox data-testid="availability-indicator" />
          ) : (
            <FillerBox />
          )}
        </DateContainer>
      ),
      disabled: !isCreateMode,
      style: {
        backgroundColor: 'white',
        color: 'inherit',
      },
    };
  };

  return {
    handleDateSelection,
    handleDayMapping,
  };
};

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor: string }>(({ backgroundColor }) => ({
  backgroundColor,
  borderRadius: '40px',
  width: '80%',
  height: '80%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));
const DateContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
}));

const AvailabilityBox = styled(Box)(({ theme }) => ({
  marginTop: '2px',
  padding: 0,
  borderRadius: '40px',
  backgroundColor: theme.palette.lwPrimaryAccentDark[60],
  alignItems: 'center',
  alignContent: 'center',
  width: '5px',
  height: '5px',
}));

const FillerBox = styled(Box)(({ theme }) => ({
  marginTop: '2px',
  padding: 0,
  borderRadius: '40px',
  backgroundColor: 'white',
  alignItems: 'center',
  alignContent: 'center',
  width: '5px',
  height: '5px',
}));
